import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaSchool,
  FaCamera,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaSearch,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
  FaImage,
  FaLink,
} from "react-icons/fa";
import { useGetSwachhataPublicReportStatsQuery, useGetSwachhataPublicReportSchoolsQuery } from "../redux/auth/authApi";
import {
  QUESTIONS,
  binsLabel,
  compostLabel,
  awarenessLabel,
  competitionLabel,
  reductionLabel,
} from "../Components/SchoolAdmin/Swachhata/swachhataSurvey";
import shsLogo from "../assets/images/swachhata/shs-logo-2026.png";
import "./SwachhataReports.css";

/*
 * /swachhata-hi-seva/events — PUBLIC "Solid Waste Management events" page (no
 * login): headline totals for the Swachhata Hi Seva 2026 drive and a gallery
 * of schools, each opening to its Q1–Q10 answers and photographs.
 *
 * The state filter lives in the URL (?state=DELHI) so a filtered view can be
 * shared. Mixed text is always ONE template string (Anuvadini merges adjacent
 * JSX text nodes and drops the spaces).
 */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const YN = { yes: "Yes", no: "No", na: "Not applicable" };
const labelList = (keys, other, labelOf) => (keys || []).map((k) => (k === "other" ? (other ? `Other: ${other}` : "Other") : labelOf(k)));

const WAVE_PATH = `M0 30 ${Array.from({ length: 4 }, (_, k) => `C${k * 600 + 100} 0 ${k * 600 + 200} 0 ${k * 600 + 300} 30 C${k * 600 + 400} 60 ${k * 600 + 500} 60 ${k * 600 + 600} 30`).join(" ")} V80 H0Z`;

// ── Small pieces ─────────────────────────────────────────────────────────────

function StateSelect({ states, value, onChange }) {
  return (
    <label className="shr-select">
      <FaMapMarkedAlt aria-hidden="true" />
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Filter by state">
        <option value="">All states and UTs</option>
        {states.map((s) => (
          <option key={s.key} value={s.key}>{`${s.name} (${fmt(s.count)})`}</option>
        ))}
        {value && !states.some((s) => s.key === value) && <option value={value}>{titleCase(value)}</option>}
      </select>
      <svg className="shr-select__chev" viewBox="0 0 20 20" aria-hidden="true">
        <path d="m5 8 5 5 5-5" />
      </svg>
    </label>
  );
}

function StatusBox({ title, text, onRetry }) {
  return (
    <div className="shr-status">
      <span className="shr-status__icon" aria-hidden="true">
        <FaSchool />
      </span>
      <b>{title}</b>
      <span>{text}</span>
      {onRetry && (
        <button type="button" className="shr-btn shr-btn--green" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

function SchoolCard({ row, index, onOpen }) {
  const a = row.answers;
  const cover = a.photos.find((p) => p.type !== "pdf");
  const tags = [
    a.binsInstalled === "all_four" && "4 bins installed",
    a.awarenessConducted === "yes" && "Awareness activity",
    a.wasteAudit === "yes" && "Waste audit",
    a.wetWasteComposted === "yes" && "Composting",
  ].filter(Boolean);
  const people = (a.students || 0) + (a.teachers || 0) + (a.community || 0);
  return (
    <button type="button" className="shr-card shr-reveal" style={{ "--d": `${(index % 12) * 0.04}s` }} onClick={() => onOpen(row)}>
      <span className="shr-card__media">
        {cover ? (
          <img src={cover.url} alt="" loading="lazy" />
        ) : (
          <span className="shr-card__nophoto" aria-hidden="true">
            <FaImage />
          </span>
        )}
        <span className="shr-card__count">
          <FaCamera aria-hidden="true" />
          {`${a.photos.length}`}
        </span>
        <span className="shr-card__view">View events</span>
      </span>
      <span className="shr-card__body">
        <span className="shr-card__name">{row.school_name}</span>
        <span className="shr-card__place">
          <FaMapMarkerAlt aria-hidden="true" />
          {[row.district, row.state].filter(Boolean).join(", ")}
        </span>
        {tags.length > 0 && (
          <span className="shr-card__tags">
            {tags.slice(0, 3).map((t) => (
              <em key={t}>{t}</em>
            ))}
          </span>
        )}
        <span className="shr-card__foot">
          <span>{`${fmt(people)} people took part`}</span>
          <span>{fmtDate(row.submittedAt)}</span>
        </span>
      </span>
    </button>
  );
}

function Chips({ list }) {
  if (!list.length) return <span className="shr-muted">None selected</span>;
  return list.map((t) => (
    <span key={t} className="shr-chip">
      {t}
    </span>
  ));
}

function ReportModal({ row, onClose }) {
  const a = row.answers;
  const images = a.photos.filter((p) => p.type !== "pdf");
  const pdfs = a.photos.filter((p) => p.type === "pdf");
  const [active, setActive] = useState(0);
  const closeRef = useRef(null);
  const go = useCallback((d) => setActive((i) => (images.length ? (i + d + images.length) % images.length : 0)), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, go]);

  const place = [row.block, row.district, row.state].filter(Boolean).join(", ");
  const pill = (v, text) => <span className={`shr-pill shr-pill--${v || "na"}`}>{text}</span>;
  const items = [
    { n: "Q1", q: QUESTIONS.q1, body: pill(a.binsInstalled === "all_four" ? "yes" : "no", binsLabel(a.binsInstalled)) },
    {
      n: "Q2",
      q: QUESTIONS.q2,
      body: (
        <>
          {pill(a.awarenessConducted, YN[a.awarenessConducted] || "—")}
          {a.awarenessConducted === "yes" && <Chips list={labelList(a.awarenessTypes, a.awarenessOther, awarenessLabel)} />}
        </>
      ),
    },
    { n: "Q3", q: QUESTIONS.q3, body: <Chips list={labelList(a.competitions, a.competitionOther, competitionLabel)} /> },
    {
      n: "Q4–6",
      q: "Number of students, teachers and community members who participated",
      body: (
        <div className="shr-mini-people">
          {[
            ["Students", a.students],
            ["Teachers", a.teachers],
            ["Community", a.community],
          ].map(([label, v]) => (
            <span key={label}>
              <b>{fmt(v)}</b>
              <small>{label}</small>
            </span>
          ))}
        </div>
      ),
    },
    { n: "Q7", q: QUESTIONS.q7, body: pill(a.wasteAudit, YN[a.wasteAudit] || "—") },
    {
      n: "Q8",
      q: QUESTIONS.q8,
      body: (
        <>
          {pill(a.wasteReduction, YN[a.wasteReduction] || "—")}
          {a.wasteReduction === "yes" && <Chips list={labelList(a.reductionActivities, a.reductionOther, reductionLabel)} />}
        </>
      ),
    },
    { n: "Q9", q: QUESTIONS.q9, body: pill(a.wetWasteComposted, compostLabel(a.wetWasteComposted)) },
  ];

  return (
    <div className="shr-modal" role="dialog" aria-modal="true" aria-label={`${row.school_name} events`} onClick={onClose}>
      <div className="shr-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="shr-dialog__head">
          <span className="shr-dialog__icon" aria-hidden="true">
            <FaSchool />
          </span>
          <div className="shr-dialog__titles">
            <h2 className="shr-dialog__title">{row.school_name}</h2>
            <div className="shr-dialog__meta">
              {place && (
                <span>
                  <FaMapMarkerAlt aria-hidden="true" />
                  {place}
                </span>
              )}
              <span>{`UDISE ${row.udise}`}</span>
              {row.submittedAt && <span>{`Shared on ${fmtDate(row.submittedAt)}`}</span>}
            </div>
          </div>
          <button ref={closeRef} type="button" className="shr-dialog__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="shr-dialog__body">
          <div className="shr-gallery">
            <span className="shr-gallery__label">{`Q10 · Photographs of campaign activities (${a.photos.length})`}</span>
            {images.length ? (
              <div className="shr-stage">
                <img key={images[active].url} src={images[active].url} alt={`${row.school_name}, photograph ${active + 1}`} />
                {images.length > 1 && (
                  <>
                    <button type="button" className="shr-stage__nav shr-stage__nav--prev" onClick={() => go(-1)} aria-label="Previous photograph">
                      <FaChevronLeft />
                    </button>
                    <button type="button" className="shr-stage__nav shr-stage__nav--next" onClick={() => go(1)} aria-label="Next photograph">
                      <FaChevronRight />
                    </button>
                    <span className="shr-stage__count">{`${active + 1} / ${images.length}`}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="shr-stage shr-stage--empty">
                <FaImage aria-hidden="true" />
                <span>This school uploaded documents only</span>
              </div>
            )}
            {(images.length > 1 || pdfs.length > 0) && (
              <div className="shr-thumbs">
                {images.map((p, k) => (
                  <button key={p.url} type="button" className={`shr-thumb ${k === active ? "is-on" : ""}`} onClick={() => setActive(k)} aria-label={`Photograph ${k + 1}`}>
                    <img src={p.url} alt="" />
                  </button>
                ))}
                {pdfs.map((p) => (
                  <a key={p.url} className="shr-thumb shr-thumb--pdf" href={p.url} target="_blank" rel="noopener noreferrer" aria-label="Open PDF document">
                    <FaFilePdf aria-hidden="true" />
                    <span>PDF</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="shr-answers">
            {items.map((it, k) => (
              <div key={it.n} className="shr-answer" style={{ "--d": `${k * 0.04}s` }}>
                <div className="shr-answer__q">
                  <span>{it.n}</span>
                  <p>{it.q}</p>
                </div>
                <div className="shr-answer__a">{it.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SwachhataReports() {
  const [params, setParams] = useSearchParams();
  const state = String(params.get("state") || "").toUpperCase();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [copied, setCopied] = useState(false);
  const [armed, setArmed] = useState(false);
  const rootRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    document.title = "Solid Waste Management Events · Swachh Paathshala: Yuva for Swachhata · Swachhata Hi Seva 2026 — Eco Clubs for Mission LiFE";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // "Show more" appends pages; any filter change starts again from page 1.
  const filterKey = `${state}|${search}`;
  const [view, setView] = useState({ key: filterKey, page: 1 });
  const page = view.key === filterKey ? view.page : 1;

  const statsQ = useGetSwachhataPublicReportStatsQuery({ state });
  const listQ = useGetSwachhataPublicReportSchoolsQuery({ state, search, page });
  const [acc, setAcc] = useState({ key: "", rows: [], total: 0, pages: 1, page: 0 });
  const cur = listQ.currentData;
  useEffect(() => {
    if (!cur?.success) return;
    setAcc((prev) => {
      const base = prev.key === filterKey && cur.page > 1 ? prev.rows : [];
      const seen = new Set(base.map((r) => r.id));
      return { key: filterKey, rows: [...base, ...cur.rows.filter((r) => !seen.has(r.id))], total: cur.total, pages: cur.pages, page: cur.page };
    });
  }, [cur, filterKey]);

  const sameFilter = acc.key === filterKey;
  const rows = useMemo(() => (sameFilter ? acc.rows : []), [sameFilter, acc.rows]);
  const hasMore = sameFilter && acc.page < acc.pages;
  const loadingMore = sameFilter && listQ.isFetching && page > acc.page;

  const stats = statsQ.data?.success ? statsQ.data : null;
  const scopeName = stats?.scope?.stateName || (state ? titleCase(state) : "");

  const collage = useMemo(
    () =>
      rows
        .map((r) => ({ row: r, photo: r.answers.photos.find((p) => p.type !== "pdf") }))
        .filter((x) => x.photo)
        .slice(0, 8),
    [rows]
  );

  // Scroll reveal; `data-shown` is set outside React so re-renders keep it.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined" || reducedMotion()) return undefined;
    setArmed(true);
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.setAttribute("data-shown", "");
            io.unobserve(en.target);
          }
        }),
      { threshold: 0.06, rootMargin: "0px 0px -30px 0px" }
    );
    root.querySelectorAll(".shr-reveal:not([data-shown])").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [stats, rows.length]);

  const pickState = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("state", v);
    else next.delete("state");
    setParams(next, { replace: true });
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      const t = document.createElement("textarea");
      t.value = url;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toGallery = () => galleryRef.current?.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
  const closeModal = useCallback(() => setOpenRow(null), []);

  const listLead = !sameFilter
    ? "Loading schools…"
    : `${fmt(acc.total)} ${acc.total === 1 ? "school" : "schools"}${scopeName ? ` in ${scopeName}` : ""}${search ? ` matching “${search}”` : ""}. Open a school to see its activities and photographs.`;

  return (
    <div ref={rootRef} className={`shr-page ${armed ? "is-armed" : ""}`}>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="shr-hero">
        <span className="shr-hero__stripe" aria-hidden="true" />
        <span className="shr-hero__leaf shr-hero__leaf--1" aria-hidden="true" />
        <span className="shr-hero__leaf shr-hero__leaf--2" aria-hidden="true" />
        <span className="shr-hero__leaf shr-hero__leaf--3" aria-hidden="true" />
        <div className="shr-wrap shr-hero__grid">
          <div className="shr-hero__copy">
            <span className="shr-hero__badge">
              <i aria-hidden="true" />
              Open to everyone
            </span>
            <h1 className="shr-hero__title">
              <span>Swachhata Hi Seva</span>
              <span className="shr-hero__year">2026</span>
            </h1>
            <p className="shr-hero__subtitle">Solid Waste Management events in schools</p>
            <p className="shr-hero__slogan">Swachh Paathshala: Yuva for Swachhata</p>
            <p className="shr-hero__sub">
              How schools across India took part in the Solid Waste Management drive: the activities they held, who joined in, and photographs from each school.
            </p>
            <div className="shr-hero__actions">
              <button type="button" className="shr-btn shr-btn--light" onClick={toGallery}>
                <FaImage aria-hidden="true" />
                <span>Browse school photos</span>
              </button>
              <Link to="/swachhata-hi-seva?start=learn" className="shr-btn shr-btn--orange">
                <span>Learning material</span>
              </Link>
              <Link to="/swachhata-hi-seva?start=quiz" className="shr-btn shr-btn--ghost">
                <span>Take the quiz</span>
              </Link>
              <button type="button" className="shr-btn shr-btn--ghost" onClick={copyLink}>
                <FaLink aria-hidden="true" />
                <span>{copied ? "Link copied" : "Copy link"}</span>
              </button>
            </div>
          </div>

          <div className="shr-hero__art" aria-hidden="true">
            {collage.slice(0, 3).map((c, k) => (
              <span key={c.row.id} className={`shr-polaroid shr-polaroid--${k}`}>
                <img src={c.photo.url} alt="" />
              </span>
            ))}
            <span className="shr-hero__logo">
              <img src={shsLogo} alt="" />
            </span>
            <span className="shr-hero__bins">
              <i />
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
        <svg className="shr-wave shr-wave--back" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true">
          <path d={WAVE_PATH} />
        </svg>
        <svg className="shr-wave" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true">
          <path d={WAVE_PATH} />
        </svg>
      </div>

      <div className="shr-wrap">
        {/* ── Gallery ────────────────────────────────────────────────────── */}
        <section className="shr-section shr-section--gallery" ref={galleryRef} aria-labelledby="shr-gallery-title">
          <div className="shr-section__head shr-reveal">
            <div>
              <span className="shr-kicker">Gallery</span>
              <h2 id="shr-gallery-title" className="shr-h2">
                Events and photographs from schools
              </h2>
              <p className="shr-section__lead">{listLead}</p>
            </div>
            <div className="shr-toolbar">
              <label className="shr-search">
                <FaSearch aria-hidden="true" />
                <input type="search" placeholder="Search by school name or UDISE" value={searchText} onChange={(e) => setSearchText(e.target.value)} aria-label="Search schools" />
              </label>
              <StateSelect states={stats?.states || []} value={state} onChange={pickState} />
            </div>
          </div>

          {listQ.isError && !rows.length ? (
            <StatusBox title="Could not load the schools" text="Please check your connection and try again." onRetry={listQ.refetch} />
          ) : !sameFilter ? (
            <div className="shr-grid">
              {Array.from({ length: 8 }, (_, k) => (
                <span key={k} className="shr-skel shr-skel--card" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <StatusBox title={search ? "No school matches your search" : "No events shared yet"} text={search ? "Try another school name or UDISE code." : "Events will appear here as schools share them."} />
          ) : (
            <div className="shr-grid">
              {rows.map((r, i) => (
                <SchoolCard key={r.id} row={r} index={i} onOpen={setOpenRow} />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="shr-more">
              <button type="button" className="shr-btn shr-btn--green" disabled={loadingMore} onClick={() => setView({ key: filterKey, page: acc.page + 1 })}>
                {loadingMore ? "Loading…" : `Show more schools (${fmt(acc.total - rows.length)} more)`}
              </button>
            </div>
          )}
        </section>
      </div>

      {openRow && <ReportModal row={openRow} onClose={closeModal} />}
    </div>
  );
}
